/**
 * Seeded daily generator: chunk-stitching (spec 6.4). NOT cell-by-cell —
 * a seeded walk over the chunk compatibility graph following a difficulty
 * envelope: calm start, rising waves, one breather, spike near the end,
 * finish. Everyone on Earth gets the identical track for a given date.
 *
 * Seed = YYYYMMDD (UTC) → mulberry32. Determinism guarantees: same date ⇒
 * same rows ⇒ same seedHash, across devices and sessions.
 */
import { mulberry32 } from "../core/rng";
import { SONGS, speedForSong, type SongAsset } from "../data/songs";
import { CHUNKS, type Chunk } from "./chunks";
import type { LevelFile } from "./types";
import { F } from "../levels/build";
import type { GravityPreset } from "../config/tuning";

export interface DailyPlanEntry {
  chunkId: string;
  difficulty: number;
  startRow: number;
}

export interface DailyTrack {
  file: LevelFile;
  seedHash: string;
  plan: DailyPlanEntry[];
  song: SongAsset;
  durationS: number;
  gravity: GravityPreset;
}

/** UTC day-of-year (1-based) — the 3-way world rotation key (amends D-024). */
export function dayOfYear(key: string): number {
  const d = new Date(`${key}T00:00:00Z`);
  const start = Date.UTC(d.getUTCFullYear(), 0, 0);
  return Math.floor((d.getTime() - start) / 86400000);
}

/** Seeded gravity preset: normal 50 %, floaty 25 %, heavy 25 % (R2 §4.3). */
export function presetFromRoll(roll: number): GravityPreset {
  if (roll < 0.5) return "normal";
  if (roll < 0.75) return "floaty";
  return "heavy";
}

/** UTC date key "YYYY-MM-DD" for a Date. */
export function dailyKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Difficulty window over track progress p ∈ [0,1] (the envelope). */
export function envelope(p: number): [number, number] {
  if (p < 0.12) return [1, 2]; // calm start
  if (p < 0.35) return [2, 3]; // first wave
  if (p < 0.55) return [3, 4]; // second wave
  if (p < 0.65) return [1, 2]; // the breather
  if (p < 0.82) return [3, 4]; // build
  if (p < 0.95) return [4, 5]; // the spike
  return [1, 3]; // taper into the finish
}

function fnvHash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

const intersects = (a: number[], b: number[]): boolean => a.some((l) => b.includes(l));

/**
 * Generate the daily track for a date key ("YYYY-MM-DD").
 * Duration target 92–98 s (hard spec bounds 90–110 s hold with worst-case
 * chunk overshoot — asserted for 365 consecutive seeds in tests).
 */
/** Rows of obstacle-light runway required before a minThrottle > 1 chunk. */
const RUNWAY_ROWS = 8;

export function generateDaily(key: string): DailyTrack {
  const seed = parseInt(key.replace(/-/g, ""), 10); // YYYYMMDD (spec 6.3)
  const rng = mulberry32(seed);

  // Daily v2 (amends D-024): 3-way world/song rotation by day-of-year, and a
  // seeded gravity preset (normal 50 / floaty 25 / heavy 25)
  const worldIdx = dayOfYear(key) % 3;
  const song = [SONGS["ion-coast"], SONGS["redline"], SONGS["duskfall"]][worldIdx]!;
  const gravity = presetFromRoll(rng());
  const speed = speedForSong(song);
  const rowsPerSecond = speed / 2; // ROW_DEPTH = 2
  const targetRows = Math.round((92 + rng() * 6) * rowsPerSecond);

  const pool = CHUNKS.filter((c) => c.gravities.includes(gravity));
  const rows: string[] = Array.from({ length: 12 }, () => F); // lead-in
  const plan: DailyPlanEntry[] = [];
  let lastExit = [0, 1, 2, 3, 4, 5, 6];
  let lastId = "";
  let rowsSinceCheckpoint = 0;
  let nextCheckpointS = 25 + rng() * 10; // 25–35 s cadence (spec 6.4)
  let maxMinThrottle = 1;

  while (rows.length < targetRows - 12) {
    const p = rows.length / targetRows;
    const [dMin, dMax] = envelope(p);
    let candidates = pool.filter(
      (c) =>
        c.difficulty >= dMin &&
        c.difficulty <= dMax &&
        c.id !== lastId &&
        intersects(lastExit, c.entry),
    );
    if (candidates.length === 0) {
      candidates = pool.filter(
        (c) =>
          c.difficulty >= dMin - 1 &&
          c.difficulty <= dMax + 1 &&
          c.id !== lastId &&
          intersects(lastExit, c.entry),
      );
    }
    if (candidates.length === 0) {
      candidates = pool.filter((c) => intersects(lastExit, c.entry));
    }
    const pick: Chunk = candidates[Math.floor(rng() * candidates.length)]!;

    // seam glue — and the runway rule (R2 §4.2): ≥ 8 obstacle-light rows
    // before any chunk that demands throttle above 1.0 (accelerating from
    // 1.0 to 1.2 takes ~0.57 s ≈ 6–9 rows)
    const glue = pick.minThrottle > 1 ? RUNWAY_ROWS : 2;
    for (let g = 0; g < glue; g++) rows.push(F);
    if (pick.minThrottle > maxMinThrottle) maxMinThrottle = pick.minThrottle;
    plan.push({ chunkId: pick.id, difficulty: pick.difficulty, startRow: rows.length });
    rows.push(...pick.rows);
    lastExit = pick.exit;
    lastId = pick.id;
    rowsSinceCheckpoint += glue + pick.rows.length;

    if (rowsSinceCheckpoint / rowsPerSecond >= nextCheckpointS && rows.length < targetRows * 0.9) {
      rows.push("CCCCCCC", F, F, F, F);
      rowsSinceCheckpoint = 0;
      nextCheckpointS = 25 + rng() * 10;
    }
  }

  rows.push(...Array.from({ length: 6 }, () => F));

  // exactly 3 bits on pure-floor rows near 25/50/75 % (center-most lane)
  for (const frac of [0.25, 0.5, 0.75]) {
    const want = Math.floor(rows.length * frac);
    for (let d = 0; d < rows.length; d++) {
      for (const idx of [want + d, want - d]) {
        if (idx > 12 && idx < rows.length - 2 && rows[idx] === F) {
          rows[idx] = "___o___";
          d = rows.length; // break outer
          break;
        }
      }
    }
  }

  rows.push("FFFFFFF");

  const world = worldIdx + 1;
  const file: LevelFile = {
    format: "skypulse-level@2",
    id: `daily-${key}`,
    name: `Daily ${key}`,
    songId: song.id,
    world,
    palette: `world${world}`,
    physics: { gravity },
    minThrottle: maxMinThrottle,
    rows,
  };
  return {
    file,
    seedHash: fnvHash(rows.join("\n")),
    plan,
    song,
    durationS: (rows.length - 1) / rowsPerSecond,
    gravity,
  };
}
